/**
 * @file 学びメモのタグの扱い (正規化・付け外し・一覧) である。
 *
 * @what: 入力したタグを NFKC で正規化して前後の空白を落とし、カンマと改行を除く。メモに足すときは
 *   重複と上限 (10個) を見る。まなぶ人のメモからタグの一覧 (件数と最終更新つき) を作る。
 * @why: タグは別々のメモを1つのテーマに繋ぐためのもので、「ＡＩ」と「AI」のような揺れが別のタグに
 *   なると繋がらない。サーバ (domain) は外部の正規化を持たないので、送る前にここでそろえる。
 *   カンマを除くのは CLI がカンマ区切りで受けるためである (plan/docs/study_learning_notes_design.md)。
 * @failure: 純関数で、失敗しない。使えないタグは null や元の並びで返す。
 */

import type { StudyNote } from "../api/notes";

/** 1件のメモに付けられるタグの数 (サーバの StudyNoteMaxTags と同じ値)。 */
export const NOTE_TAGS_MAX = 10;
/** タグ1つの長さの上限 (サーバの StudyNoteTagMaxLen と同じ値)。 */
export const NOTE_TAG_MAX_LEN = 30;

/** 入力したタグを正規化する。空になるときは null を返す。 */
export function normalizeNoteTag(raw: string): string | null {
  const tag = raw
    .normalize("NFKC")
    .replace(/[,\r\n]/g, "")
    .trim();
  if (tag === "") return null;
  return Array.from(tag).slice(0, NOTE_TAG_MAX_LEN).join("").trim();
}

/** tags に tag を足した並びを返す。正規化して空・重複・上限のときは元の並びのまま返す。 */
export function addNoteTag(tags: readonly string[], raw: string): readonly string[] {
  const tag = normalizeNoteTag(raw);
  if (tag === null || tags.includes(tag) || tags.length >= NOTE_TAGS_MAX) return tags;
  return [...tags, tag];
}

/** tags から tag を外した並びを返す。 */
export function removeNoteTag(tags: readonly string[], tag: string): readonly string[] {
  return tags.filter((t) => t !== tag);
}

export interface NoteTagSummary {
  tag: string;
  count: number;
  /** そのタグの付いたメモの、最も新しい更新日時 (ISO)。 */
  lastUpdated: string;
}

/** メモからタグの一覧を作る。最終更新の新しい順で、同じなら名前の順に並べる。 */
export function summarizeNoteTags(notes: readonly StudyNote[]): NoteTagSummary[] {
  const byTag = new Map<string, NoteTagSummary>();
  for (const note of notes) {
    for (const tag of note.tags ?? []) {
      const prev = byTag.get(tag);
      if (prev === undefined) {
        byTag.set(tag, { tag, count: 1, lastUpdated: note.updated_at });
      } else {
        prev.count += 1;
        if (note.updated_at > prev.lastUpdated) prev.lastUpdated = note.updated_at;
      }
    }
  }
  return [...byTag.values()].sort((a, b) =>
    a.lastUpdated === b.lastUpdated
      ? a.tag.localeCompare(b.tag, "ja")
      : a.lastUpdated < b.lastUpdated
        ? 1
        : -1,
  );
}
